/**
 * This file may contain code that uses generative AI for code assistance, unit testing and/or entire functions.
 * The generative model(s) used may be a combination of GitHub Copilot, OpenAI ChatGPT or others.
 */

export const COMMAND_INTELLIGENCE_FONT_STACK =
  '"Work Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

export const COMMAND_INTELLIGENCE_CONTENT_MAX_WIDTH_PX = 800;

const validate = (theme, values) => theme?.validateMode?.(values) ?? values.light;

/**
 * @param {import('@mui/material').Theme & { validateMode?: Function }} theme
 */
export const getCommandIntelligenceTokens = theme => {
  const canvasGradient = validate(theme, {
    light: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%)',
    dark: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
  });

  const surfaceGlass = validate(theme, {
    light: 'rgba(255, 255, 255, 0.85)',
    dark: 'rgba(30, 41, 59, 0.85)',
  });

  const surfaceMain = validate(theme, {
    light: 'rgba(255, 255, 255, 0.9)',
    dark: 'rgba(15, 23, 42, 0.9)',
  });

  const surfaceChrome = validate(theme, {
    light: 'rgba(248, 250, 252, 0.96)',
    dark: 'rgba(15, 23, 42, 0.96)',
  });

  const surfaceInput = validate(theme, {
    light: 'rgba(255, 255, 255, 0.95)',
    dark: 'rgba(15, 23, 42, 0.95)',
  });

  const surfaceContent = validate(theme, {
    light: '#ffffff',
    dark: 'rgba(30, 41, 59, 0.95)',
  });

  const borderGlass = validate(theme, {
    light: 'rgba(255, 255, 255, 0.8)',
    dark: 'rgba(71, 85, 105, 0.3)',
  });

  const borderSubtle = validate(theme, {
    light: 'rgba(15, 23, 42, 0.08)',
    dark: 'rgba(148, 163, 184, 0.18)',
  });

  const borderInput = validate(theme, {
    light: 'rgba(226, 232, 240, 0.6)',
    dark: 'rgba(51, 65, 85, 0.4)',
  });

  const textPrimary = validate(theme, {
    light: '#0f172a',
    dark: '#e2e8f0',
  });

  const textSecondary = validate(theme, {
    light: '#334155',
    dark: '#cbd5e1',
  });

  const textMuted = validate(theme, {
    light: '#64748b',
    dark: '#94a3b8',
  });

  const accentPrimary = validate(theme, {
    light: '#3b82f6',
    dark: '#60a5fa',
  });

  const accentIntelligence = validate(theme, {
    light: '#7c3aed',
    dark: '#a78bfa',
  });

  const accentIntelligenceShimmer = validate(theme, {
    light: 'rgba(167, 139, 250, 0.85)',
    dark: 'rgba(221, 214, 254, 0.75)',
  });

  const gradientCta = validate(theme, {
    light: 'linear-gradient(165deg, #2563eb 0%, #0F62F8 35%, #004BD4 100%)',
    dark: 'linear-gradient(165deg, #3b82f6 0%, #2563eb 40%, #004BD4 100%)',
  });

  const bubbleUserBg = validate(theme, {
    light: '#e8f1ff',
    dark: 'rgba(59, 130, 246, 0.25)',
  });

  const bubbleUserBorder = validate(theme, {
    light: '#c7ddff',
    dark: 'rgba(96, 165, 250, 0.35)',
  });

  const bubbleUserText = validate(theme, {
    light: '#0b2545',
    dark: '#e2e8f0',
  });

  const focusRing = validate(theme, {
    light: '0 0 0 4px rgba(38, 132, 255, 0.1)',
    dark: '0 0 0 4px rgba(38, 132, 255, 0.12)',
  });

  const elevationGlassPanel = validate(theme, {
    light: '0 8px 32px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04), inset 0 1px 0 rgba(255, 255, 255, 0.6)',
    dark: '0 8px 32px rgba(0, 0, 0, 0.4), 0 2px 8px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.05)',
  });

  const elevationDrawer = validate(theme, {
    light: '4px 0 24px rgba(0, 0, 0, 0.12)',
    dark: '4px 0 24px rgba(0, 0, 0, 0.5)',
  });

  const elevationDock = validate(theme, {
    light: '0 4px 20px rgba(0, 0, 0, 0.06)',
    dark: '0 4px 20px rgba(0, 0, 0, 0.25)',
  });

  const elevationInputFocus = validate(theme, {
    light: '0 12px 48px rgba(38, 132, 255, 0.12), 0 0 0 4px rgba(38, 132, 255, 0.08)',
    dark: '0 12px 48px rgba(38, 132, 255, 0.2), 0 0 0 4px rgba(38, 132, 255, 0.12)',
  });

  const elevationCta = validate(theme, {
    light: 'inset 0 1px 0 rgba(255, 255, 255, 0.25), 0 3px 6px rgba(15, 98, 248, 0.28)',
    dark: 'inset 0 1px 0 rgba(255, 255, 255, 0.12), 0 3px 6px rgba(59, 130, 246, 0.35)',
  });

  const elevationCtaHover = validate(theme, {
    light: 'inset 0 1px 0 rgba(255, 255, 255, 0.25), 0 6px 12px rgba(15, 98, 248, 0.35)',
    dark: 'inset 0 1px 0 rgba(255, 255, 255, 0.12), 0 6px 12px rgba(59, 130, 246, 0.45)',
  });

  const statusErrorBg = validate(theme, {
    light: 'rgba(239, 68, 68, 0.08)',
    dark: 'rgba(239, 68, 68, 0.15)',
  });

  const sendDisabledBg = validate(theme, {
    light: '#e2e8f0',
    dark: '#334155',
  });

  const sendDisabledColor = validate(theme, {
    light: '#94a3b8',
    dark: '#64748b',
  });

  const ctaForeground = '#ffffff';

  const stopButtonBg = validate(theme, {
    light: '#0f172a',
    dark: '#e2e8f0',
  });

  const stopButtonFg = validate(theme, {
    light: '#ffffff',
    dark: '#0f172a',
  });

  return {
    fontStack: COMMAND_INTELLIGENCE_FONT_STACK,
    contentMaxWidthPx: COMMAND_INTELLIGENCE_CONTENT_MAX_WIDTH_PX,
    canvasGradient,
    surfaceGlass,
    surfaceMain,
    surfaceChrome,
    surfaceInput,
    surfaceContent,
    borderGlass,
    borderSubtle,
    borderInput,
    textPrimary,
    textSecondary,
    textMuted,
    accentPrimary,
    accentIntelligence,
    accentIntelligenceShimmer,
    gradientCta,
    bubbleUserBg,
    bubbleUserBorder,
    bubbleUserText,
    focusRing,
    elevationGlassPanel,
    elevationDrawer,
    elevationDock,
    elevationInputFocus,
    elevationCta,
    elevationCtaHover,
    statusErrorBg,
    sendDisabledBg,
    sendDisabledColor,
    ctaForeground,
    stopButtonBg,
    stopButtonFg,
    blurShell: 'blur(20px)',
    blurChrome: 'blur(12px)',
    radiusCard: '16px',
    radiusCardSm: '14px',
    radiusDialog: '20px',
    radiusBubble: '14px',
    radiusChip: '8px',
    radiusPill: '9999px',
    motionEasing: 'cubic-bezier(0.16, 1, 0.3, 1)',
    motionSpring: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
  };
};

/**
 * @param {import('@mui/material').Theme & { validateMode?: Function }} theme
 */
export const getGradientCtaSx = theme => {
  const tokens = getCommandIntelligenceTokens(theme);

  return {
    background: tokens.gradientCta,
    color: tokens.ctaForeground,
    boxShadow: tokens.elevationCta,
    '&:hover': {
      background: tokens.gradientCta,
      boxShadow: tokens.elevationCtaHover,
    },
    '&.Mui-disabled': {
      background: tokens.sendDisabledBg,
      color: tokens.sendDisabledColor,
      boxShadow: 'none',
    },
  };
};
